import {
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  Unsubscribe,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, firestoreDb } from '../client';
import { AuthStatus, Role, SyntraxisUser } from '../types/types';
import { authenticateUser } from './firebase-service';
import { fetchBusinessAccount } from '../google-auth';
import {
  AdminsCollection,
  LocationsCollection,
  UsersCollection,
} from '../collections';
import {
  fetchDocuments,
  updateDocument,
  updateDocuments,
} from '../firebase-utils';
import { BusinessAccount, BusinessLocation } from '../types/business-types';
import { logAxiosError } from 'src/utils/logger/logs';
// import { UsersCollection } from '../collections';

export interface AuthListener {
  // eslint-disable-next-line no-unused-vars
  onUserUpdated: (user: SyntraxisUser) => void;
  // eslint-disable-next-line no-unused-vars
  onError: (error: Error) => void;
}

class AuthService {
  private user: SyntraxisUser | undefined;
  private listener: AuthListener | undefined;
  private token: string | undefined;
  private authUnsubscribe: Unsubscribe | undefined;

  constructor() {
    this.authUnsubscribe = this.addStateChangeListener();
  }

  public async signInWithGoogle() {
    try {
      if (this.authUnsubscribe) {
        this.authUnsubscribe();
      }
      const provider = new GoogleAuthProvider();
      provider.addScope('https://www.googleapis.com/auth/business.manage');
      const result = await signInWithPopup(auth, provider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      this.token = credential?.accessToken;
      this.authUnsubscribe = this.addStateChangeListener();
    } catch (error) {
      this.listener?.onError(error as Error);
    }
  }

  public signOut() {
    return auth.signOut();
  }

  public getUser() {
    return this.user;
  }

  public addListener(listener: AuthListener) {
    this.listener = listener;
    return () => {
      this.listener = undefined;
    };
  }

  /**
   *
   * @private
   *
   */

  private notifyListeners() {
    if (!this.listener || !this.user) {
      return;
    }

    this.listener.onUserUpdated(this.user);
  }

  private async updateUser(user: SyntraxisUser) {
    const effectiveToken = this.token ?? user.token;
    if (effectiveToken) {
      // make sure in-memory token is set for this session as well
      this.token = effectiveToken;
      let account: BusinessAccount | undefined;
      let locations: BusinessLocation[] | undefined;
      try {
        const result = await fetchBusinessAccount(effectiveToken);
        account = result.account;
        locations = result.locations;
        if (result.unauthenticated) {
          // Google token invalid/expired; clear token but keep app session
          this.token = undefined;
          user.token = undefined;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        logAxiosError(err);
      }
      if (account) {
        user.account = account;
      }

      user.token = effectiveToken;

      // Determine subscription status for this user
      let hasActiveSubscription = false;
      try {
        const subRef = doc(
          firestoreDb,
          `${UsersCollection}/${user.key}/billing/subscription`,
        );
        const snap = await getDoc(subRef);
        if (snap.exists()) {
          const status = (snap.data()?.status as string) || 'none';
          hasActiveSubscription = status === 'trialing' || status === 'active';
        }
      } catch (_e) {
        // On any error, treat as no active subscription
        hasActiveSubscription = false;
      }

      // Base locations from Google (preferred) or existing user locations
      const ensureBusinessIds = (
        loc: BusinessLocation,
        ownerId: string,
      ): string[] => {
        const existing = Array.isArray(loc.businessIds) ? loc.businessIds : [];
        return Array.from(new Set([...existing, ownerId]));
      };

      const baseLocations: BusinessLocation[] =
        locations && locations.length > 0 ? locations : user.locations || [];

      // Normalize with businessIds including this user
      const normalizedLocations: BusinessLocation[] = baseLocations.map(
        location => ({
          ...location,
          businessIds: ensureBusinessIds(location, user.key),
        }),
      );

      // Propagate aggregated businessIds onto the user record for quick lookup
      user.businessIds = Array.from(
        new Set(normalizedLocations.flatMap(loc => loc.key)),
      );

      // Apply subscription gating:
      // - No active/trial subscription: all locations disabled for this user
      // - Active/trial subscription: all locations enabled for now
      //   (can later be limited by max allowed locations per plan)
      const allLocationsWithFlags: BusinessLocation[] = normalizedLocations.map(
        location => ({
          ...location,
          disabled: !hasActiveSubscription,
        }),
      );

      // Expose all locations to the UI with their disabled flag,
      // so components like LocationSelect can render them but prevent selection.
      user.locations = allLocationsWithFlags;

      const userLocationCollectionId = `${UsersCollection}/${user.key}/${LocationsCollection}`;
      await updateDocuments<BusinessLocation>(
        userLocationCollectionId,
        allLocationsWithFlags,
      );

      // Global locations collection: write canonical data without per-user disabled flag
      await updateDocuments<BusinessLocation>(
        LocationsCollection,
        normalizedLocations,
      );

      if (user.role === Role.admin) {
        const { data } =
          await fetchDocuments<BusinessLocation>(LocationsCollection);
        user.locations =
          data?.map(loc => ({
            ...loc,
            businessIds: ensureBusinessIds(loc, user.key),
          })) ?? [];

        const userDocumentId = `${AdminsCollection}/${user.key}`;
        await updateDocument(
          {
            token: effectiveToken,
            account: user.account,
            businessIds: user.businessIds,
          },
          userDocumentId,
        );
      } else {
        const userDocumentId = `${UsersCollection}/${user.key}`;
        await updateDocument(
          {
            token: effectiveToken,
            account: user.account,
            businessIds: user.businessIds,
          },
          userDocumentId,
        );
      }
    }
    this.setUser(user);
  }

  private setUser(user: SyntraxisUser) {
    this.user = user;
    this.notifyListeners();
  }

  private addStateChangeListener(): Unsubscribe {
    return onAuthStateChanged(auth, async user => {
      try {
        if (!user) {
          // Signed out: clear user state and exit without error
          this.user = undefined;
          this.token = undefined;
          this.notifyListeners();
          return;
        }
        const { isAuthenticated, data: userResult } =
          await authenticateUser(user);
        if (!isAuthenticated || !userResult) {
          throw new Error('Authentication failed', {
            cause: AuthStatus.notFound,
          });
        }
        this.updateUser(userResult);
      } catch (error) {
        this.listener?.onError(error as Error);
      }
    });
  }
}

const authService = new AuthService();
export { authService };
